/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { useState, createRef, createContext, useContext } from 'react';
import { Fields } from 'lib/proforma';
import * as t from 'lib/t';
import api from 'lib/api';
import Present from 'lib/Present';
import { EmailT, PasswordT } from 'types/tcomb';
import { css, jsx } from '@emotion/core';
import history from 'lib/history';
import MemberFormLogo from './MemberFormLogo';
import MemberFormSubmitBtn from './MemberFormSubmitBtn';
import MemberFormAltLink from './MemberFormAltLink';
import MemberFormErrorOutput from './MemberFormErrorOutput';
import MemberPasswordRequest from './MemberPasswordRequest';
import Turbolinks from 'turbolinks';
import { decamelizeKeys } from 'humps';
import utilGetCommunityBaseUrl from './utilGetCommunityBaseUrl';
import useThemeColor from './useThemeColor';
import inIframe from 'lib/inIframe';

const MemberSignInContext = createContext({});

const EmailField = EmailT.opt({
  type: 'email',
  attrs: { placeholder: 'Email' },
});

type ValueT = { email: ?string, password: ?string };

export function createSession(
  value: ValueT
): Promise<{ next?: string, success: boolean, ... } & any> {
  const communityUser = decamelizeKeys(value);
  return api.fetchJson('/sign_in', {
    method: 'POST',
    body: { community_user: communityUser },
  });
}

export function MemberSignInForm({
  onForgotPasswordRequested,
  onSuccess,
  email,
}: {
  onForgotPasswordRequested?: () => any,
  onSuccess?: ({ success: boolean, next?: string }) => any,
  email?: ?string,
}): Element<string> {
  const { companyId } = useContextState();

  function ForgotPassordLink() {
    if (!onForgotPasswordRequested) return null;
    return (
      <MemberFormAltLink onClick={onForgotPasswordRequested}>
        Forgot your password?
      </MemberFormAltLink>
    );
  }

  const type = t.struct({
    email: EmailField,
    password: PasswordT.opt({
      type: 'password',
      attrs: { placeholder: 'Password' },
    }),
  });

  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState();
  const [value, setValue] = useState({
    email,
    password: null,
    company_id: companyId,
  });
  const myRef = createRef();

  function onSubmit() {
    const el = myRef.current;
    if (!onSuccess) return;
    if (!el) return;
    const errs = el.validate().errors;
    if (Present.is(errs)) {
      setErrors();
      return;
    }

    setLoading(true);

    createSession(value)
      .then((res) => {
        setErrors();
        onSuccess(res);
      })
      .catch((e) => {
        if (e.error) {
          setErrors([e.error]);
        } else if (Present.is(e)) {
          setErrors([JSON.stringify(e)]);
        } else {
          setErrors(['Unknown server error.']);
        }
        setLoading(false);
      });
  }

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      css={css('label { display: none; }')}
    >
      <MemberFormErrorOutput errors={errors} />
      <Fields
        ref={myRef}
        value={value}
        type={type}
        onChange={(v) => setValue(v)}
      />
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <ForgotPassordLink />
        </div>
        <MemberFormSubmitBtn
          width="100px"
          isLoading={isLoading}
          onClick={onSubmit}
        >
          Login
        </MemberFormSubmitBtn>
      </div>
    </form>
  );
}

function MemberJoinButton() {
  // const baseUrl = utilGetCommunityBaseUrl();
  // function onClick() {
  //   Turbolinks.visit(baseUrl + '/onboarding');
  // }

  return null;

  // return (
  //   <div data-testid="MemberJoinButton" className="mb-5 pt-5 text-center">
  //     <button
  //       onClick={onClick}
  //       className="btn font-weight-bold pl-4 pr-4 pt-2 pb-2 rounded w-100"
  //       css={css(`
  //         color: var(--dashboard-primary-color);
  //       `)}
  //     >
  //       Become a member
  //     </button>
  //   </div>
  // );
}

function MemberSignInSection(props) {
  const id = props.id || 1;
  const baseUrl = utilGetCommunityBaseUrl();

  return (
    <div data-testid="MemberSignInSection">
      <MemberFormLogo id={id} href={baseUrl} />
      <div className="d-flex mt-3 justify-content-center">
        <div>
          <div className="text-muted text-right">beta</div>
          <h2 data-testid="Title" className="text-center mt-0">
            Member Portal
          </h2>
        </div>
      </div>
      <MemberSignInForm {...props} />
      <MemberJoinButton />
    </div>
  );
}

function getComponent(mode) {
  if (mode === 'LOGIN') {
    return MemberSignInSection;
  } else if (mode === 'FORGOT_PASSWORD') {
    return MemberPasswordRequest;
  }
  return () => null;
}

function useContextState() {
  return useContext(MemberSignInContext).state;
}

function MemberSignInStateManager() {
  const [mode, setMode] = useState('LOGIN');
  const Component = getComponent(mode);
  const ctx = useContext(MemberSignInContext);
  const { successUrl, companyId } = ctx.state;
  const id = companyId || 1;

  function onSuccess(res: $Shape<{ success: boolean, next: string }> = {}) {
    const next = history.getKey('next');
    const url = res.next || next || successUrl || '/dashboard';
    Turbolinks.visit(url);
  }

  function onForgotPasswordRequested() {
    // TODO: update path to password_reset
    setMode('FORGOT_PASSWORD');
  }

  function onLoginRequested() {
    // TODO: update path to sign_in
    setMode('LOGIN');
  }

  return (
    <Component
      id={id}
      onForgotPasswordRequested={onForgotPasswordRequested}
      onLoginRequested={onLoginRequested}
      onSuccess={onSuccess}
    />
  );
}

function Container(props: { successUrl?: string, companyId?: ID }) {
  const state = { companyId: props.companyId, successUrl: props.successUrl };
  const ctx = { state };
  const isEmbedded = inIframe();
  const { data: themeColor } = useThemeColor();

  return (
    <MemberSignInContext.Provider value={ctx}>
      <div
        data-testid="MemberSignIn"
        className="container"
        css={css(`
        --dashboard-primary-color: ${themeColor};
      `)}
      >
        <div
          style={{ height: isEmbedded ? '60vh' : '100vh' }}
          className="row align-items-center justify-content-center"
        >
          <div className="col-md-4">
            <MemberSignInStateManager />
          </div>
        </div>
      </div>
    </MemberSignInContext.Provider>
  );
}

export default (Container: any);
